import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Card, CardContent, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { SvgIconComponent } from '@mui/icons-material';
import type { ReactNode } from 'react';
import { IconTile, toneColor, type Tone } from './IconTile';

interface StatCardProps {
  label: string;
  /** The figure itself, already formatted — usually a `<MoneyText>`. */
  value: ReactNode;
  /** One line under the figure: a count, a share, a qualifier. */
  caption?: ReactNode;
  /** Shown behind an info icon beside the label, for a figure that needs a definition. */
  help?: string;
  icon?: SvgIconComponent;
  tone?: Tone;
  /**
   * A fraction between 0 and 1, drawn as a bar across the foot of the card.
   *
   * Only for figures that genuinely have a whole to be a part of — stops
   * visited out of stops planned, routes finished out of routes started. A bar
   * under a revenue figure would be inventing a target nobody set.
   */
  progress?: number;
  loading?: boolean;
}

/**
 * One figure on the dashboard.
 *
 * The label sits above the number and the caption below it, so a column of
 * these reads down the middle. `help` exists because several of these figures
 * are only unambiguous once defined — "collected" means money received in the
 * window, against any document, and a dashboard that does not say so invites
 * someone to subtract it from sales.
 *
 * The tile on the right carries the tone, and the tone is the point: eight
 * cards of identical grey text are eight cards nobody tells apart, and the
 * eye goes to the amber one. So amber is spent on what is owed, and nothing
 * else on this screen is allowed to have it.
 */
export function StatCard({
  label,
  value,
  caption,
  help,
  icon,
  tone = 'neutral',
  progress,
  loading,
}: StatCardProps) {
  const theme = useTheme();
  const colour = toneColor(theme, tone);

  return (
    <Card sx={{ height: '100%', position: 'relative', overflow: 'hidden' }}>
      <CardContent sx={{ pb: progress === undefined ? 2 : 2.5 }}>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                {label}
              </Typography>
              {help ? (
                <Tooltip title={help}>
                  <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                </Tooltip>
              ) : null}
            </Stack>

            {loading ? (
              <Skeleton width="60%" height={32} />
            ) : (
              <Typography variant="h2" component="div" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {value}
              </Typography>
            )}

            {caption ? (
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}
              >
                {loading ? <Skeleton width="40%" /> : caption}
              </Typography>
            ) : null}
          </Box>

          {icon ? <IconTile icon={icon} tone={tone} size={40} /> : null}
        </Stack>
      </CardContent>

      {progress !== undefined && !loading ? (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: 4,
            backgroundColor: alpha(colour, 0.15),
          }}
        >
          <Box
            sx={{
              height: '100%',
              width: `${Math.round(Math.min(Math.max(progress, 0), 1) * 100)}%`,
              backgroundColor: colour,
            }}
          />
        </Box>
      ) : null}
    </Card>
  );
}
